/**
 * Order History Page (Web)
 * Displays order history with filtering and status tracking
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only displays order data.
 * All order operations are handled by backend Order Service.
 */

import { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  Menu,
  Divider,
  Grid,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useOrderHistory } from '@shared/features/orders';
import { formatCurrency, formatDateTime } from '@shared/utils/formatters';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { useAppDispatch } from '@shared/store/hooks';
import { fetchOrder } from '@shared/store/slices/ordersSlice';

export function OrderHistoryPage() {
  const dispatch = useAppDispatch();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const { orders, loading, error, refresh, cancel } = useOrderHistory({
    status: statusFilter || undefined,
    autoRefreshInterval: 30000, // Auto-refresh every 30 seconds
  });

  const handleCancelOrder = async () => {
    if (cancelOrderId) {
      try {
        await cancel(cancelOrderId);
        setCancelDialogOpen(false);
        setCancelOrderId(null);
      } catch (err) {
        // Error handled by hook
      }
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, orderId: string) => {
    setMenuAnchor(event.currentTarget);
    setSelectedOrderId(orderId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedOrderId(null);
  };

  const handleViewDetails = async () => {
    if (selectedOrderId) {
      setDetailsDialogOpen(true);
      setLoadingDetails(true);
      handleMenuClose();
      
      try {
        const result = await dispatch(fetchOrder(selectedOrderId)).unwrap();
        setOrderDetails(result);
      } catch (err) {
        console.error('Failed to fetch order details:', err);
        setOrderDetails(null);
      } finally {
        setLoadingDetails(false);
      }
    }
  };

  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
    setOrderDetails(null);
  };

  const getStatusChip = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'filled' || statusLower === 'executed' || statusLower === 'complete') {
      return <Chip icon={<CheckCircleIcon />} label={status} color="success" size="small" sx={{ fontSize: '0.7rem', height: 24 }} />;
    } else if (statusLower === 'pending' || statusLower === 'open') {
      return <Chip icon={<PendingIcon />} label={status} color="warning" size="small" sx={{ fontSize: '0.7rem', height: 24 }} />;
    } else if (statusLower === 'rejected' || statusLower === 'cancelled' || statusLower === 'failed') {
      return <Chip icon={<ErrorIcon />} label={status} color="error" size="small" sx={{ fontSize: '0.7rem', height: 24 }} />;
    }
    return <Chip label={status} size="small" sx={{ fontSize: '0.7rem', height: 24 }} />;
  };

  const getSideColor = (side: string) => {
    const sideLower = side.toLowerCase();
    return sideLower === 'buy' ? 'success.main' : 'error.main';
  };

  return (
    <AuthenticatedLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Filter by Status"
                  sx={{
                    height: '32px',
                    fontSize: '0.75rem',
                    '& .MuiSelect-select': {
                      fontSize: '0.75rem',
                      py: '4px',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          fontSize: '0.75rem',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                  <MenuItem value="pending" sx={{ fontSize: '0.75rem' }}>Pending</MenuItem>
                  <MenuItem value="filled" sx={{ fontSize: '0.75rem' }}>Filled</MenuItem>
                  <MenuItem value="executed" sx={{ fontSize: '0.75rem' }}>Executed</MenuItem>
                  <MenuItem value="rejected" sx={{ fontSize: '0.75rem' }}>Rejected</MenuItem>
                  <MenuItem value="cancelled" sx={{ fontSize: '0.75rem' }}>Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => refresh()}
                  disabled={loading}
                  color="primary"
                  size="small"
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'primary.main',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                  }}
                >
                  <RefreshIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>

              {statusFilter && (
                <Button
                  size="small"
                  onClick={() => {
                    setStatusFilter('');
                  }}
                  startIcon={<ClearIcon sx={{ fontSize: '0.875rem' }} />}
                  sx={{ 
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                    height: '32px',
                    px: 1.5,
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && orders.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No Orders Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No orders found for the selected filters.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
            <Table 
              sx={{ 
                minWidth: 650,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                },
              }} 
              aria-label="orders table" 
              size="small"
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Created At
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Instrument
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Side
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Quantity
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Avg Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Order Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Broker
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Menu
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.6875rem', py: 1 }}>
                    Cancel
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => {
                  const canCancel =
                    order.status.toLowerCase() === 'pending' ||
                    order.status.toLowerCase() === 'open' ||
                    order.status.toLowerCase() === 'submitted';
                  return (
                    <TableRow
                      key={order.platform_order_id}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: 'rgba(4, 55, 123, 0.015)' },
                        '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.02)' },
                        '&:last-of-type td': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
                      }}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {formatDateTime(order.created_at, { includeTime: true })}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {order.instrument_symbol || order.instrument_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: getSideColor(order.side), fontWeight: 'medium', fontSize: '0.75rem' }}>
                          {order.side.toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {parseFloat(order.filled_quantity).toLocaleString()}/{parseFloat(order.quantity).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {order.price ? formatCurrency(parseFloat(order.price)) : 'Market'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip label={order.order_type} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 24 }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        {getStatusChip(order.status)}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                          {order.broker_type || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Tooltip title="More options">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, order.platform_order_id)}
                            sx={{ p: 0.5 }}
                          >
                            <MoreVertIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {canCancel ? (
                          <Tooltip title="Cancel Order">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setCancelOrderId(order.platform_order_id);
                                setCancelDialogOpen(true);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <CancelIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleViewDetails} sx={{ fontSize: '0.875rem' }}>
            <InfoIcon sx={{ mr: 1, fontSize: '1rem' }} />
            View Details
          </MenuItem>
        </Menu>

        {/* Order Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={handleDetailsDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Order Details
          </DialogTitle>
          <DialogContent>
            {loadingDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : orderDetails ? (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* Platform Order Details */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          Platform Order Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Platform Order ID</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {orderDetails.platform_order_id}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Broker Order ID</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {orderDetails.broker_order_id || orderDetails.broker_order?.broker_order_id || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Client Order ID</Typography>
                            <Typography variant="body2">
                              {orderDetails.client_order_id || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Instrument ID</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {orderDetails.instrument_id}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Side</Typography>
                            <Typography variant="body2" sx={{ color: getSideColor(orderDetails.side), fontWeight: 'medium' }}>
                              {orderDetails.side.toUpperCase()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Quantity</Typography>
                            <Typography variant="body2">
                              {parseFloat(orderDetails.quantity).toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Filled Quantity</Typography>
                            <Typography variant="body2">
                              {parseFloat(orderDetails.filled_quantity).toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Remaining Quantity</Typography>
                            <Typography variant="body2">
                              {parseFloat(orderDetails.remaining_quantity).toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Order Type</Typography>
                            <Typography variant="body2">{orderDetails.order_type}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Time in Force</Typography>
                            <Typography variant="body2">{orderDetails.time_in_force}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Price</Typography>
                            <Typography variant="body2">
                              {orderDetails.price ? formatCurrency(parseFloat(orderDetails.price)) : 'Market'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box sx={{ mt: 0.5 }}>{getStatusChip(orderDetails.status)}</Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Created At</Typography>
                            <Typography variant="body2">
                              {formatDateTime(orderDetails.created_at, { includeTime: true })}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Updated At</Typography>
                            <Typography variant="body2">
                              {formatDateTime(orderDetails.updated_at, { includeTime: true })}
                            </Typography>
                          </Grid>
                          {orderDetails.notes && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Notes</Typography>
                              <Typography variant="body2">{orderDetails.notes}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Broker Order Details */}
                  {orderDetails.broker_order && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Broker Order Details
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Broker Order ID</Typography>
                              <Typography variant="body2">{orderDetails.broker_order.broker_order_id}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Broker Type</Typography>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {orderDetails.broker_order.broker_type}
                              </Typography>
                            </Grid>
                            {orderDetails.broker_order.broker_account_id && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Broker Account ID</Typography>
                                <Typography variant="body2">{orderDetails.broker_order.broker_account_id}</Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.exchange_id && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Exchange ID</Typography>
                                <Typography variant="body2">{orderDetails.broker_order.exchange_id}</Typography>
                              </Grid>
                            )}
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Broker Status</Typography>
                              <Typography variant="body2">{orderDetails.broker_order.broker_status || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Sync Status</Typography>
                              <Chip 
                                label={orderDetails.broker_order.sync_status} 
                                size="small" 
                                color={
                                  orderDetails.broker_order.sync_status === 'synced' ? 'success' :
                                  orderDetails.broker_order.sync_status === 'failed' ? 'error' : 'warning'
                                }
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Grid>
                            {orderDetails.broker_order.broker_quantity && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Broker Quantity</Typography>
                                <Typography variant="body2">
                                  {parseFloat(orderDetails.broker_order.broker_quantity).toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.broker_filled_quantity && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Broker Filled Quantity</Typography>
                                <Typography variant="body2">
                                  {parseFloat(orderDetails.broker_order.broker_filled_quantity).toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.broker_average_price && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Broker Average Price</Typography>
                                <Typography variant="body2">
                                  {formatCurrency(parseFloat(orderDetails.broker_order.broker_average_price))}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.broker_fees && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Broker Fees</Typography>
                                <Typography variant="body2">
                                  {formatCurrency(parseFloat(orderDetails.broker_order.broker_fees))}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.broker_taxes && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Broker Taxes</Typography>
                                <Typography variant="body2">
                                  {formatCurrency(parseFloat(orderDetails.broker_order.broker_taxes))}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.broker_commission && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Broker Commission</Typography>
                                <Typography variant="body2">
                                  {formatCurrency(parseFloat(orderDetails.broker_order.broker_commission))}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.broker_other_charges && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Other Charges</Typography>
                                <Typography variant="body2">
                                  {formatCurrency(parseFloat(orderDetails.broker_order.broker_other_charges))}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.last_sync_at && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Last Sync At</Typography>
                                <Typography variant="body2">
                                  {formatDateTime(orderDetails.broker_order.last_sync_at, { includeTime: true })}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.sync_attempts !== undefined && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Sync Attempts</Typography>
                                <Typography variant="body2">{orderDetails.broker_order.sync_attempts}</Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.sync_error && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">Sync Error</Typography>
                                <Typography variant="body2" color="error.main">
                                  {orderDetails.broker_order.sync_error}
                                </Typography>
                              </Grid>
                            )}
                            {orderDetails.broker_order.broker_message && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">Broker Message</Typography>
                                <Typography variant="body2">{orderDetails.broker_order.broker_message}</Typography>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Fills */}
                  {orderDetails.fills && orderDetails.fills.length > 0 && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Execution Fills ({orderDetails.fills.length})
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Price</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Quantity</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Time</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Venue</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {orderDetails.fills.map((fill: any, index: number) => (
                                <TableRow key={index}>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    {formatCurrency(parseFloat(fill.execution_price))}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                    {parseFloat(fill.execution_quantity).toLocaleString()}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    {formatDateTime(fill.execution_time, { includeTime: true })}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>{fill.venue || 'N/A'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to load order details. Please try again.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailsDialogClose}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Order Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Cancel Order</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel this order? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
            <Button onClick={handleCancelOrder} color="error" variant="contained">
              Yes, Cancel Order
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AuthenticatedLayout>
  );
}
